import LZString from 'lz-string';
import _ from 'lodash';
import { CustomFieldFilterConfig, FilterConfig } from 'App/babel/filterConfigs/FilterConfig';
import { ListViewFilter } from 'App/resources/AllIWant';
import CustomField from 'App/resources/Model/CustomField';
import history from 'App/pages/routes/history';
import logError from 'Helpers/logError';
import AppService from 'App/legacy/AppService';
import appConfig from 'App/babel/config';
import ComparisonTypes from 'Resources/ComparisonTypes';
import Filter from 'App/babel/filters/Filter';
import { mergeFilters as mergeSegmentFilters } from 'App/services/SegmentService';
import {
	getConfigName,
	isAddress,
	isCategory,
	match as matchWithAttrs,
	parseFilters,
	parseNested,
	getCustomConfig as getCustomConfigBase,
	isCustom,
	isCustomCategory
} from 'App/helpers/filterHelper';

/*
	Filter *instances* — creating them, serialising them to a URL and restoring them again.

	The sibling App/helpers/filterHelper.ts owns filter *configs* and matching. These two split the
	surface of the Angular FilterHelper service
	(ui/app/upsales/common/components/listFilters/FilterHelper.js), which delegates here so both
	trees share one implementation (WEB-7776).

	Every function takes its filter configs through `options.getConfig`. Callers that hold their own
	configs pass them; the rest fall back to the `getConfig` below, which reads the same
	`ListAttributes` registry the Angular service read, so the port is behaviour-identical.

	That fallback is the module's only Angular dependency, and it is confined to one function rather
	than spread across call sites. See setListAttributesProvider for how it goes away.
*/

type GetConfig = (filterName: string, type: string) => FilterConfig | null;

type Options = {
	getConfig: GetConfig;
};

type ListAttributesLike = {
	get: (type: string) => { attr: { [k: string]: any }; standardFilters: { [k: string]: any } };
};

let listAttributesProvider: (() => ListAttributesLike) | null = null;

/*
	Supplies the attribute registry that getConfig reads.

	The registry is the `ListAttributes` Angular factory, which fans out to ~18k lines of attribute
	services across 57 files. Those definitions are the only source of standard filter configs -
	App/babel/filterConfigs/* is a separate, smaller set (contact has 96 filters in the Angular
	attributes against 63 in filterConfigs, so substituting it would silently drop filters from
	saved views and urls). Reading the same registry Angular reads is what makes this port
	behaviour-identical.

	The Angular FilterHelper registers its own injected ListAttributes at boot, so that provider is
	what production uses. The fallback reads the same object off the Tools global, which tools.js
	publishes at :307 - deliberately not the getAngularModule injector bridge, which would add to
	WEB-7833's scope and is unavailable under jest.

	When the attributes layer is ported, register a native provider here and both the fallback and
	the Angular dependency disappear without touching any caller.
*/
export const setListAttributesProvider = (provider: (() => ListAttributesLike) | null) => {
	listAttributesProvider = provider;
};

const getListAttributes = (): ListAttributesLike => {
	if (listAttributesProvider) {
		return listAttributesProvider();
	}
	const fromTools = (window as unknown as { Tools?: { ListAttributes?: ListAttributesLike } })?.Tools?.ListAttributes;
	if (!fromTools) {
		throw new Error(
			'FilterHelper: no ListAttributes registered. Call setListAttributesProvider before resolving filter configs.'
		);
	}
	return fromTools;
};

type CustomFieldConfigBuilder = (options: {
	entityType?: string;
	name: string;
	field: CustomField;
	fieldId: number;
}) => CustomFieldFilterConfig;

let customFieldConfigProvider: CustomFieldConfigBuilder | null = null;

/*
	Supplies the builder getConfigsFromCustomFields turns each custom field into.

	Same seam as setListAttributesProvider, and for the same reason: the Angular service's
	getCustomFieldConfig and the one exported by App/helpers/filterHelper.ts are parallel
	implementations that have drifted (the Angular one offers the hasValue/hasNoValue date presets and
	derives a task field's lookupType; the filterHelper.ts one sets a dataPromise that makes
	LegacyFilters read a Select field's configured defaults instead of its looked-up values). The
	filterHelper.ts version is what ProductSearch and the product admin already use, so it is not the
	one to reach for here - resolving the Angular builder keeps this port behaviour-identical and
	leaves those callers alone. Reconciling the two is its own piece of work.
*/
export const setCustomFieldConfigProvider = (provider: CustomFieldConfigBuilder | null) => {
	customFieldConfigProvider = provider;
};

const getCustomFieldConfigBuilder = (): CustomFieldConfigBuilder => {
	if (customFieldConfigProvider) {
		return customFieldConfigProvider;
	}
	const fromTools = (
		window as unknown as { Tools?: { FilterHelper?: { getCustomFieldConfig?: CustomFieldConfigBuilder } } }
	)?.Tools?.FilterHelper?.getCustomFieldConfig;
	if (!fromTools) {
		throw new Error(
			'FilterHelper: no custom field config builder registered. Call setCustomFieldConfigProvider before building custom field configs.'
		);
	}
	return fromTools;
};

/*
	A faithful port of the Angular service's getCustomConfig wrapper.

	It differs from the getCustomConfig exported by App/helpers/filterHelper.ts by resolving the
	custom fields from AppService when the caller passes none and the filter name is prefixed
	(`client.Custom_5`). Callers such as TableHead rely on that fallback — they pass a
	`customFields` that can be undefined — so switching them straight to the filterHelper.ts export
	would lose the lookup and return a config without its `$field`.
*/
export const getCustomConfig = (filterName: string, customFields?: CustomField[]) => {
	let fields = customFields;

	// This needs to be moved to the listUdo component when we port it
	if (filterName && !fields && filterName.indexOf('.') !== -1 && filterName.indexOf('userDefinedObject') === -1) {
		// The prefix is whatever the filter name carries at runtime, so it cannot be narrowed to
		// AppService's entity union statically. The original passed it through untyped.
		const customType = filterName.split('.')[0] as Parameters<typeof AppService.getCustomFields>[0];
		fields = AppService.getCustomFields(customType);
	}

	return getCustomConfigBase(filterName, fields);
};

/*
	A faithful port of the Angular service's getConfig, and the default config source for every
	function in this module. Callers that already hold their own filter configs should keep passing
	`options.getConfig` - this is the fallback for the ones that relied on the Angular default.
*/
export const getConfig: GetConfig = (name, type) => {
	let config;
	let customType = type + '';

	// Maybe this can be done in some other place
	if (type === 'assignedLeads' || type === 'unassignedLeads') {
		type = 'account';
	}

	if (type === 'opportunity') {
		type = 'order';
	}

	if (type.indexOf('userDefinedObject') === 0) {
		const parsedNr = parseInt(type[type.length - 1]);
		const nr = isNaN(parsedNr) ? '' : parsedNr;
		customType = 'userDefined' + nr;
		type = type.slice(0, -1);
	}

	if (isCustom(name)) {
		const nameType = name.split('.')[0];

		if (customType === 'agreement' && name.indexOf('Agreement') === -1) {
			customType = 'order';
		} else if (
			type.indexOf('userDefinedObject') === -1 &&
			['Agreement', 'Activity', 'Appointment'].every(t => name.indexOf(t) === -1) &&
			name.indexOf('Custom_') !== 0 &&
			nameType !== type
		) {
			customType = nameType;
		} else if (customType === 'campaign') {
			customType = 'project';
		}

		config = getCustomConfig(name, AppService.getCustomFields(customType as any));
	} else {
		const standardFilters = getListAttributes().get(type).standardFilters;
		const filterConfig = standardFilters[name];

		if (filterConfig instanceof Filter) {
			// Raw filters keep their behaviour on the prototype, so copy rather than clone
			config = Object.assign(Object.create(Object.getPrototypeOf(filterConfig)), filterConfig);
		} else {
			config = _.cloneDeep(filterConfig);
		}
	}

	if (config) {
		config.filterName = name;
		if (config.isExcludeFilter) {
			config.comparisonType = 'NotEquals';
		}
	} else if (appConfig.ENV === 'DEV' && console && console.warn) {
		console.warn('No filterconfig found named "' + name + '" in type', type);
	}

	return config ?? null;
};

const resolveGetConfig = (options?: Partial<Options>): GetConfig => options?.getConfig ?? getConfig;

/*
	The Angular service's match signature, which takes an entity *type* and looks the attributes up
	itself. App/helpers/filterHelper.ts exports a match that takes the resolved `attrs` map instead;
	this resolves them from the same registry and delegates, so there is one matching implementation.
*/
export const match = (filters: any, obj: any, type: string, throwIfNoAttr?: boolean) => {
	if (!filters || !filters.length) {
		return true;
	}
	return matchWithAttrs(filters, obj, getListAttributes().get(type).attr, throwIfNoAttr);
};

type UrlFilter = {
	n?: string;
	c?: string;
	v?: any;
	d?: string;
	i?: number;
};

/*
	`allowMultipleSelections` is not declared on FilterConfig - it is one of the undeclared properties
	the Angular filter configs carry - so this takes the single field it reads rather than the whole
	config type.
*/
export const getFilterName = (filterName: string, config: FilterConfig | { allowMultipleSelections?: boolean }) =>
	(config as { allowMultipleSelections?: boolean }).allowMultipleSelections
		? filterName + '_fix_' + new Date().valueOf()
		: filterName;

/*
	A faithful port of the Angular service's isInactiveValue.

	App/helpers/filterHelper.ts exports a function of the same name, but it is explicitly documented
	there as "not a 1-1 port" and it does differ: it has no early return on `filter.inactive`, treats
	a radio filter as inactive when its value is null (the Angular one always reports radio as
	active), and applies different rules to empty ranges. Callers that relied on the Angular
	semantics - including convertForURL's `skipInactive` option, used by AdvancedSearch and by
	Filter2/editSegment in the Angular tree - need these semantics, not those.

	Keep both until the difference is deliberately reconciled; do not "deduplicate" them.
*/
export const isInactiveValue = (
	activeFilter: ListViewFilter | null | undefined,
	type: string,
	filterConfig?: FilterConfig | null,
	options?: Partial<Options>
): boolean => {
	if (!activeFilter || !type) {
		return true;
	}

	let config = filterConfig;
	if (!config) {
		const configName = getConfigName(activeFilter.filterName);
		config = resolveGetConfig(options)(configName, type);
	}

	if (!config) {
		return true;
	}

	// Raw filters
	if (config.type === 'raw') {
		if ((config as any).isInactive) {
			return (config as any).isInactive(activeFilter);
		}
		return !!activeFilter.inactive;
	}

	if (activeFilter.inactive) {
		return true;
	}

	// change to only type when all old filters are gone
	let inputType = ((config as any).inputType || config.type)?.toLowerCase();

	if (!inputType) {
		return true;
	}

	const cfField = (config as CustomFieldFilterConfig).$field;
	if (isCustom(config.filterName as string) && cfField && cfField.datatype) {
		inputType = cfField.datatype.toLowerCase();
	}

	// Empty multi selects
	if (Array.isArray(activeFilter.value) && !activeFilter.value.length) {
		return true;
	}

	// Inactive booleans
	if (inputType === 'boolean') {
		return activeFilter.value === null;
	}

	if (inputType === 'radio') {
		// If this gets here it is active
		return false;
	}

	if (inputType === 'time') {
		if (!activeFilter.value.start && !activeFilter.value.end) {
			return true;
		}
	}

	/*
		Empty range filters.

		'dateRange' is intentionally left in its original camelCase. `inputType` is lowercased above,
		so this entry can never match - the branch is dead for dateRange filters, which therefore
		fall through and are reported active. Normalising it to 'daterange' would "fix" that and
		start reporting whenever-preset date ranges as inactive, which would silently drop them from
		the urls convertForURL builds. Preserved deliberately; changing it is a behaviour change that
		needs its own ticket.
	*/
	if (
		['date', 'integer', 'calculation', 'currency', 'discount', 'percent', 'dateRange', 'range'].includes(
			inputType
		) &&
		(activeFilter.value.preset === 'whenever' ||
			(activeFilter.value.start === null &&
				activeFilter.value.end === null &&
				activeFilter.value.preset !== 'hasValue' &&
				activeFilter.value.preset !== 'hasNoValue'))
	) {
		return true;
	}

	// Empty text filters and other filters
	if (activeFilter.value === '' || !activeFilter.value) {
		return true;
	}

	return false;
};

export const isAdCampaign = (name: string) => name.indexOf('adCampaign.id_') === 0;

const ICONS: { [key: string]: { icon: string; tooltip: string } } = {
	'default.scoreVisits': { icon: 'Icon Icon-globe', tooltip: 'leads.scoreVisitsTooltip' },
	'default.scoreMail': { icon: 'Icon Icon-envelope', tooltip: 'leads.scoreMailTooltip' },
	'default.scoreForm': { icon: 'Icon Icon-form', tooltip: 'leads.scoreFormTooltip' },
	'default.scoreMarketingCustom': { icon: 'Icon Icon-bullseye', tooltip: 'leads.scoreMarketingCustom' },
	'default.countVisits': { icon: 'Icon Icon-globe', tooltip: 'leads.countVisitsTooltip' },
	'default.countMail': { icon: 'Icon Icon-envelope', tooltip: 'leads.countMailTooltip' },
	'default.countForm': { icon: 'Icon Icon-form', tooltip: 'leads.countFormTooltip' },
	'default.countMarketingCustom': { icon: 'Icon Icon-bullseye', tooltip: 'leads.countMarketingCustom' },
	'default.contactCount': { icon: 'Icon Icon-users', tooltip: 'leads.contactCountTooltip' }
};

export const useIcon = (name: string) => ICONS[name];

export const getAddress = (name: string) => {
	const spl = name.split('_');
	const type = spl[1];
	let field = '';
	if (spl.length === 3) {
		field = spl[2] + '.';
	}
	return {
		title: 'address.' + field + type.toLowerCase(),
		key: name
	};
};

/** Returns a new raw filter for `name`, shaped by its config's type. */
export const filter = (name: string, type: string, options?: Partial<Options>) => {
	const resolved = resolveGetConfig(options);
	const configName = getConfigName(name);
	const filterConfig = resolved(configName, type);
	if (!filterConfig) {
		return null;
	}

	let newFilter: any;

	switch (filterConfig.type) {
		case 'raw':
			newFilter = (filterConfig as any).generate();
			break;

		default:
		case 'normal':
			newFilter = { value: '', comparisonType: 'Equals' };
			break;

		case 'custom': {
			const field = (filterConfig as CustomFieldFilterConfig).$field;
			if (field) {
				newFilter = { value: null, comparisonType: 'Equals' };

				switch (field.datatype) {
					case 'Integer':
					case 'Calculation':
					case 'Currency':
					case 'Discount':
					case 'Percent':
					case 'Time':
						newFilter.value = { start: null, end: null };
						break;

					case 'Date':
						newFilter.value = { start: null, end: null, preset: 'whenever' };
						newFilter.comparisonType = null;
						break;

					case 'Boolean':
						newFilter.value = null;
						break;
					case 'User':
						newFilter.value = '';
						break;
					case 'Users':
						newFilter.value = [];
						break;

					case 'Text':
					case 'String':
					case 'Link':
					case 'Email':
					case 'Phone':
						newFilter.comparisonType = 'Wildcard';
						break;
				}

				newFilter.dataType = field.datatype;
			} else {
				newFilter = { value: '', comparisonType: 'Equals' };
			}
			break;
		}

		case 'list':
		case 'listShort':
			newFilter = { value: [], comparisonType: (filterConfig as any).comparisonType || 'Equals' };
			break;

		case 'dateRange':
			newFilter = { value: { start: null, end: null, preset: 'whenever' }, comparisonType: null };
			break;

		case 'range':
			newFilter = { value: { start: null, end: null }, comparisonType: null };
			break;

		case 'radio':
			newFilter = { comparisonType: null };
			break;

		case 'boolean':
		case 'notNull':
			newFilter = { value: true, comparisonType: null };
			break;
	}

	newFilter.filterName = getFilterName(name, filterConfig);
	newFilter.columnPath = (filterConfig as any).columnPath;

	if (isCustomCategory(name) || isCustom(name)) {
		newFilter.columnPath = name;
	}

	if (!newFilter.dataType) {
		newFilter.dataType = filterConfig.type;
	}

	if (filterConfig.type !== 'raw') {
		newFilter.field = (filterConfig as any).field;
	}

	return newFilter;
};

const mapSort = (sort: { attribute: string }, type: string, options?: Partial<Options>) => {
	if (isCustom(sort.attribute)) {
		const configName = getConfigName(sort.attribute);
		const config = resolveGetConfig(options)(configName, type) as CustomFieldFilterConfig | null;

		if (config && config.$field) {
			switch (config.$field.datatype) {
				case 'Date':
					sort.attribute = sort.attribute + '.valueDate';
					break;
				case 'Time':
					sort.attribute = sort.attribute + '.valueTime';
					break;
				case 'Boolean':
					sort.attribute = sort.attribute + '.valueBoolean';
					break;
				case 'Integer':
				case 'Calculation':
					sort.attribute = sort.attribute + '.valueInteger';
					break;
				case 'Currency':
				case 'Percent':
				case 'Discount':
					sort.attribute = sort.attribute + '.valueDouble';
					break;
			}
		}
	}
	return sort;
};

export const parseSort = (
	sort: { attribute: string } | { attribute: string }[],
	type: string,
	options?: Partial<Options>
) => {
	const sortCopy = _.cloneDeep(sort);

	if (Array.isArray(sortCopy)) {
		return sortCopy.map(s => mapSort(s, type, options));
	}
	return mapSort(sortCopy, type, options);
};

/** Converts a filter object to a compressed, url-friendly string. */
export const convertForURL = (
	// Partial: callers may pass a bare { value } - the function never reads filterName, because the
	// config is always resolved from the map key.
	rawFilters: { [name: string]: Partial<ListViewFilter> },
	type: string,
	options?: Partial<Options> & { skipInactive?: boolean }
) => {
	const resolved = resolveGetConfig(options);
	const filters: UrlFilter[] = [];

	Object.keys(rawFilters || {}).forEach(name => {
		const rawFilter = rawFilters[name] as any;
		const configName = getConfigName(name);
		const filterConfig = resolved(configName, type);

		if (!filterConfig) {
			// The original dereferenced filterConfig.type here and threw. Dropping the filter keeps the
			// rest of the url usable, but it means the url no longer round-trips - report it rather
			// than losing a filter silently.
			logError(new Error('Missing filter config'), `Cannot serialise filter "${name}" of type "${type}" to url`);
			return;
		}
		if (options?.skipInactive && isInactiveValue(rawFilter, type, filterConfig, options)) {
			return;
		}

		let f: UrlFilter | undefined;

		switch (filterConfig.type) {
			case 'raw':
				if ((filterConfig as any).toUrl) {
					f = (filterConfig as any).toUrl(rawFilter);
				} else {
					f = { v: rawFilter.value };
				}
				break;

			case 'dateRange': {
				const value: any = {};
				value.preset = rawFilter.value.preset;
				if (value.preset === 'custom') {
					value.start = rawFilter.value.start;
					value.end = rawFilter.value.end;
				}
				if (rawFilter.value.status) {
					value.status = rawFilter.value.status;
				}
				if (value.preset?.startsWith('lastX') || value.preset?.startsWith('nextX')) {
					value.start = rawFilter.value.start;
				}

				f = { v: value };
				break;
			}

			case 'text':
				if (rawFilter.value) {
					f = { c: rawFilter.comparisonType, v: rawFilter.value };
				}
				break;

			default:
			case 'list':
			case 'listShort':
			case 'normal':
			case 'range':
			case 'boolean':
				f = { c: rawFilter.comparisonType, v: rawFilter.value };
				if (rawFilter.inactive) {
					f.i = 1;
				} else if (Array.isArray(rawFilter.value) && !rawFilter.value.length) {
					f = undefined;
				}
				break;

			case 'radio':
				f = { c: rawFilter.comparisonType, v: rawFilter.value };
				if (rawFilter.inactive) {
					f.i = 1;
				}
				break;

			case 'notNull':
				f = { c: rawFilter.comparisonType, v: rawFilter.value };
				break;

			case 'custom':
				f = { c: rawFilter.comparisonType, v: rawFilter.value, d: rawFilter.dataType };
				if (rawFilter.inactive) {
					f.i = 1;
				}
				break;
		}

		if (f) {
			f.n = name;
			filters.push(f);
		}
	});

	if (!filters.length) {
		return '';
	}

	return encodeURIComponent(LZString.compressToBase64(JSON.stringify(filters)));
};

/** Parses a url string produced by convertForURL back into filters. */
export const parseString = (string: string | undefined | null, type: string, options?: Partial<Options>) => {
	const resolved = resolveGetConfig(options);
	const filters: { [name: string]: any } = {};
	let query: UrlFilter[] = [];

	if (!string || string === '') {
		return filters;
	}

	const decompressedString = LZString.decompressFromBase64(decodeURIComponent(string));

	if (decompressedString && typeof decompressedString === 'string') {
		try {
			query = JSON.parse(decompressedString);
		} catch (e) {
			logError(e, 'Failed to parse decompressed string');
		}
	}

	if (!Array.isArray(query)) {
		query = [query];
	}

	query.forEach(urlFilter => {
		const configName = getConfigName(urlFilter.n as string);
		const c = resolved(configName, type) as any;
		if (!c) {
			return; // continue if filter was not found
		}
		let newFilter = filter(urlFilter.n as string, type, options);
		if (c.type === 'raw' && c.fromUrl) {
			newFilter = c.fromUrl(urlFilter);
		} else {
			if (
				(c.type === 'dateRange' || (c.type === 'custom' && c.displayType === 'dateRange')) &&
				urlFilter.v.preset === 'custom'
			) {
				if (urlFilter.v.start) {
					newFilter.value.start = new Date(urlFilter.v.start);
				}
				if (urlFilter.v.end) {
					newFilter.value.end = new Date(urlFilter.v.end);
				}
				newFilter.value.preset = urlFilter.v.preset;

				if (urlFilter.v.status) {
					newFilter.value.status = urlFilter.v.status;
				}
			} else {
				newFilter.value = urlFilter.v;
			}
			if (urlFilter.c) {
				newFilter.comparisonType = urlFilter.c;
			}
			if (
				(c.type === 'idList' && urlFilter.i) ||
				(c.type === 'list' && urlFilter.v.length === 0) ||
				(c.type === 'listShort' && urlFilter.v.length === 0) ||
				(c.type === 'radio' && urlFilter.i) ||
				(c.type === 'custom' && urlFilter.i)
			) {
				newFilter.inactive = true;
			}
			if (urlFilter.d) {
				newFilter.dataType = urlFilter.d;
			}
		}
		newFilter.filterName = urlFilter.n;
		filters[urlFilter.n as string] = newFilter;
	});

	return filters;
};

/** Returns the filters encoded in the current url's `q` param. */
export const parseFromURL = (type: string, options?: Partial<Options>) => {
	const q = new URLSearchParams(history.location.search).get('q');

	return parseString(q, type, options);
};

/** Rebuilds filter instances from the filters stored on a saved view. */
export const parseViewFilters = (viewFilters: ListViewFilter[], type: string, options?: Partial<Options>) => {
	const resolved = resolveGetConfig(options);
	const filters: { [name: string]: any } = {};

	// _.forEach, not Array.prototype.forEach: the Angular original used angular.forEach, which also
	// iterates a filterName-keyed object.
	_.forEach(_.cloneDeep(viewFilters || []), (viewFilter: any) => {
		const configName = getConfigName(viewFilter.filterName);
		const filterConfig = resolved(configName, type);
		const newFilter = filter(viewFilter.filterName, type, options);

		if (!filterConfig || !newFilter) {
			return;
		}

		newFilter.value = viewFilter.value;
		if (viewFilter.comparisonType) {
			newFilter.comparisonType = viewFilter.comparisonType;
		}

		if ((filterConfig.type === 'list' || filterConfig.type === 'listShort') && viewFilter.value.length === 0) {
			newFilter.inactive = true;
		} else {
			newFilter.inactive = viewFilter.inactive !== undefined ? viewFilter.inactive : false;
		}
		filters[viewFilter.filterName] = newFilter;
	});

	return filters;
};

/** Builds a filterName-keyed map of custom-field filter configs. */
export const getConfigsFromCustomFields = (
	entityType: string | undefined,
	customFields: CustomField[],
	ignoreEntityType?: boolean
) => {
	const buildConfig = getCustomFieldConfigBuilder();

	// _.reduce, not Array.prototype.reduce: the Angular original used lodash, which iterates a keyed
	// object as well as an array.
	return _.reduce<CustomField, { [filterName: string]: CustomFieldFilterConfig }>(
		customFields || [],
		(res, cf) => {
			const filterName =
				(ignoreEntityType ? 'Custom_' : entityType ? entityType + '.Custom_' : 'Custom_') + cf.id;
			const config = buildConfig({
				entityType,
				name: filterName,
				field: cf,
				fieldId: cf.id
			});
			res[config.filterName as string] = config;
			return res;
		},
		{}
	);
};

/*
	The Angular FilterHelper's public surface, for the legacy `tools` bridge objects that older
	ReactTemplates components receive as a prop and call methods on. New code should import the
	individual functions instead.

	This has to stay a drop-in for `Tools.FilterHelper`: the components it reaches are untyped .jsx,
	so a missing member or a differently-defaulted one surfaces as a runtime error or a silently empty
	result rather than a compile error. Members whose defaults differ from the Angular service's are
	bound below rather than passed through.
*/
export const filterHelperSurface = {
	ComparisonType: ComparisonTypes,
	convertForURL,
	filter,
	getAddress,
	getConfig,
	getConfigName,
	getConfigsFromCustomFields,
	getCustomConfig,
	// Resolved per call: the Angular builder, not the filterHelper.ts one. See
	// setCustomFieldConfigProvider.
	getCustomFieldConfig: ((options: Parameters<CustomFieldConfigBuilder>[0]) =>
		getCustomFieldConfigBuilder()(options)) as CustomFieldConfigBuilder,
	getFilterName,
	isAdCampaign,
	isAddress,
	isCategory,
	isCustom,
	isCustomCategory,
	isInactiveValue,
	match,
	mergeSegmentFilters,
	// filterHelper.ts's parseFilters defaults options.getConfig to `() => null`, which drops every
	// filter. The Angular service defaulted to its ListAttributes-backed resolver, so bind that.
	parseFilters: (
		filters: Parameters<typeof parseFilters>[0],
		type: string,
		rb?: Parameters<typeof parseFilters>[2],
		fieldPrefix?: Parameters<typeof parseFilters>[3],
		options?: Parameters<typeof parseFilters>[4]
	) => parseFilters(filters, type, rb ?? null, fieldPrefix ?? null, options ?? { getConfig }),
	parseFromURL,
	parseNested,
	parseSort,
	parseString,
	parseViewFilters,
	useIcon
};
